import { useCallback, useEffect, useRef, useState } from 'react';

import PriceParrotService from '~/services/PriceParrotService';

import { PriceSummaryJobStatus } from '~/types/services/priceParrot';

import { parseOtaCsv } from './otaParity/parseOtaCsv';
import { ParsedOtaCsv } from './otaParity/types';

const POLL_INTERVAL_MS = 5000;
const JOB_FETCH_LIMIT = 100;
const MAX_JOB_NOT_FOUND_RETRIES = 6; // ~30s grace for a just-submitted job to appear in the list.
const RECENT_JOB_WINDOW_MS = 15 * 60 * 1000;

export type OtaShopStatus = 'idle' | 'submitting' | 'scraping' | 'ready' | 'error';

export interface SubmitScrapeInput {
  locationUrl: string;
  nights: number;
  startDate: string;
  endDate: string;
  currencyCode: string;
  countryCode: string;
}

export interface UseOtaRateShopOrchestrationResult {
  status: OtaShopStatus;
  progressText: string;
  error: string | null;
  clearError: () => void;
  parsedCsv: ParsedOtaCsv | null;
  submitScrape: (input: SubmitScrapeInput) => Promise<void>;
  loadMostRecentJob: (locationUrl: string, currencyCode: string, countryCode: string) => Promise<void>;
  cleanup: () => void;
}

/** Submit-and-poll for a single job (not the multi-job fan-out or SSE patterns its siblings use). */
export function useOtaRateShopOrchestration(): UseOtaRateShopOrchestrationResult {
  const [status, setStatus] = useState<OtaShopStatus>('idle');
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [parsedCsv, setParsedCsv] = useState<ParsedOtaCsv | null>(null);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const clearError = useCallback(() => setError(null), []);

  const downloadAndParse = useCallback(async (jobId: string) => {
    const response = await PriceParrotService.downloadPriceSummaryJob(jobId);
    const csvText = await response.text();
    return parseOtaCsv(csvText);
  }, []);

  // Shared poll-until-COMPLETED logic for both a freshly submitted job and a resumed in-progress one.
  const pollJob = useCallback(
    (jobId: string) => {
      let notFoundAttempts = 0;
      const poll = async () => {
        try {
          const { data: jobs } = await PriceParrotService.getPriceSummaryJobs({ limit: JOB_FETCH_LIMIT });
          const job = jobs.find((j) => j.jobId === jobId);

          if (!job) {
            notFoundAttempts += 1;
            if (notFoundAttempts > MAX_JOB_NOT_FOUND_RETRIES) {
              setStatus('error');
              setError('Lost track of the scrape job — it may have aged out of the recent jobs list.');
              return;
            }
            pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
            return;
          }

          if (job.status === PriceSummaryJobStatus.COMPLETED) {
            const parsed = await downloadAndParse(jobId);
            setParsedCsv(parsed);
            setStatus('ready');
            return;
          }

          if (job.status === PriceSummaryJobStatus.FAILED || job.status === PriceSummaryJobStatus.CANCELLED) {
            setStatus('error');
            setError(`Scrape job ${job.status.toLowerCase()}`);
            return;
          }

          const progressPct = job.days > 0 ? Math.floor((job.progress / job.days) * 100) : 0;
          setProgressText(`Scraping OTA rates... (${progressPct}%)`);
          pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        } catch (e) {
          setStatus('error');
          setError(e instanceof Error ? e.message : String(e));
        }
      };
      pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    },
    [downloadAndParse],
  );

  const submitScrape = useCallback(
    async (input: SubmitScrapeInput) => {
      cleanup();
      try {
        setStatus('submitting');
        setError(null);
        setParsedCsv(null);
        setProgressText('Submitting scrape job...');

        const {
          result: { job_id: jobId },
        } = await PriceParrotService.postPriceSummaryJob({
          location_url: input.locationUrl,
          start_date: input.startDate,
          end_date: input.endDate,
          number_of_nights: input.nights,
          currency_code: input.currencyCode,
          country_code: input.countryCode,
        });

        setStatus('scraping');
        setProgressText('Scraping OTA rates...');
        pollJob(jobId);
      } catch (e) {
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [cleanup, pollJob],
  );

  const loadMostRecentJob = useCallback(
    async (locationUrl: string, currencyCode: string, countryCode: string) => {
      cleanup();
      try {
        setStatus('scraping');
        setError(null);
        setParsedCsv(null);
        setProgressText('Looking for an existing scrape...');

        const { data: jobs } = await PriceParrotService.getPriceSummaryJobs({ limit: JOB_FETCH_LIMIT });
        const matchingJobs = jobs.filter(
          (j) => j.locationUrl === locationUrl && j.currencyCode === currencyCode && j.countryCode === countryCode,
        );

        const latestCompleted = matchingJobs
          .filter((j) => j.status === PriceSummaryJobStatus.COMPLETED)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

        const recentInProgress = matchingJobs
          .filter(
            (j) =>
              (j.status === PriceSummaryJobStatus.PROCESSING || j.status === PriceSummaryJobStatus.RETRYING) &&
              Date.now() - new Date(j.createdAt).getTime() < RECENT_JOB_WINDOW_MS,
          )
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

        // Prefer a job genuinely being worked on right now over an older completed report, but only
        // within a recency window — an old stuck job must never block reaching a completed one.
        if (recentInProgress && (!latestCompleted || recentInProgress.createdAt > latestCompleted.createdAt)) {
          setProgressText('Scraping OTA rates...');
          pollJob(recentInProgress.jobId);
          return;
        }

        if (latestCompleted) {
          const parsed = await downloadAndParse(latestCompleted.jobId);
          setParsedCsv(parsed);
          setStatus('ready');
          return;
        }

        setStatus('error');
        setError('No scrape found for this property. Try a fresh scrape instead.');
      } catch (e) {
        setStatus('error');
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [cleanup, downloadAndParse, pollJob],
  );

  return { status, progressText, error, clearError, parsedCsv, submitScrape, loadMostRecentJob, cleanup };
}
